import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, Bell, FileText, CalendarDays } from "lucide-react";

const upcomingEvents = [
  {
    title: "Annual Sports Day",
    date: "January 15, 2025",
    time: "9:00 AM - 4:00 PM",
    location: "School Sports Ground",
    description: "A day of athletic competitions featuring track & field events, team sports, and fun activities for all grades.",
    image: "https://images.unsplash.com/photo-1461896836934-eba09b295a37?auto=format&fit=crop&w=600&q=80",
    type: "Sports",
  },
  {
    title: "Science Exhibition",
    date: "January 25, 2025",
    time: "10:00 AM - 3:00 PM",
    location: "School Auditorium",
    description: "Students showcase innovative science projects, models, and experiments. Open to parents and visitors.",
    image: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=600&q=80",
    type: "Academic",
  },
  {
    title: "Republic Day Celebration",
    date: "January 26, 2025",
    time: "8:00 AM - 12:00 PM",
    location: "School Ground",
    description: "Flag hoisting ceremony followed by cultural performances, patriotic songs, and special assembly.",
    image: "https://images.unsplash.com/photo-1606761568499-6d2451b23c66?auto=format&fit=crop&w=600&q=80",
    type: "Cultural",
  },
  {
    title: "Parent-Teacher Meeting",
    date: "February 5, 2025",
    time: "9:00 AM - 1:00 PM",
    location: "Respective Classrooms",
    description: "Discuss student progress, academic performance, and development with class teachers.",
    image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=600&q=80",
    type: "Academic",
  },
  {
    title: "Inter-School Quiz Competition",
    date: "February 15, 2025",
    time: "10:00 AM - 2:00 PM",
    location: "School Auditorium",
    description: "Annual quiz competition featuring teams from various schools in the region.",
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=600&q=80",
    type: "Competition",
  },
  {
    title: "Annual Day Function",
    date: "March 10, 2025",
    time: "5:00 PM - 9:00 PM",
    location: "School Auditorium",
    description: "Grand celebration featuring cultural performances, prize distribution, and special recognitions.",
    image: "https://images.unsplash.com/photo-1472289065668-ce650ac443d2?auto=format&fit=crop&w=600&q=80",
    type: "Cultural",
  },
];

const announcements = [
  {
    title: "Winter Vacation",
    date: "December 25, 2024 - January 5, 2025",
    description: "School will remain closed for winter vacation. Classes resume on January 6, 2025.",
  },
  {
    title: "Fee Payment Reminder",
    date: "January 10, 2025",
    description: "Last date for quarterly fee payment without late fee charges.",
  },
  {
    title: "Uniform Update",
    date: "January 2025",
    description: "New winter uniform is now available at the school store.",
  },
  {
    title: "Bus Route Changes",
    date: "Effective January 15, 2025",
    description: "Some bus routes will be modified. Parents will receive detailed information via SMS.",
  },
];

const holidays2025 = [
  { date: "January 26", occasion: "Republic Day" },
  { date: "March 14", occasion: "Holi" },
  { date: "April 14", occasion: "Ambedkar Jayanti" },
  { date: "April 18", occasion: "Good Friday" },
  { date: "May 1", occasion: "May Day" },
  { date: "August 15", occasion: "Independence Day" },
  { date: "October 2", occasion: "Gandhi Jayanti" },
  { date: "October 12", occasion: "Dussehra" },
  { date: "October 31", occasion: "Diwali" },
  { date: "November 15", occasion: "Guru Nanak Jayanti" },
  { date: "December 25", occasion: "Christmas" },
];

const Events = () => {
  return (
    <div data-testid="events-page">
      {/* Page Header */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Events & News
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Stay updated with the latest happenings at ID4U School
            </p>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              What's Happening
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Upcoming Events
            </h2>
            <div className="section-divider mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcomingEvents.map((event, index) => (
              <motion.div
                key={event.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-secondary text-white text-xs font-medium px-3 py-1 rounded-full">
                    {event.type}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-3">
                    {event.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{event.description}</p>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-primary" />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-primary" />
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-primary" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements & Holiday List */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Announcements */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Bell className="text-primary" size={24} />
                </div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Important Notices
                </h2>
              </div>

              <div className="space-y-4">
                {announcements.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-4 rounded-xl border border-slate-200"
                  >
                    <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                    <p className="text-xs text-secondary font-medium mb-2">{item.date}</p>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Holiday List */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                  <CalendarDays className="text-secondary" size={24} />
                </div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Holiday List 2025
                </h2>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Date</th>
                      <th className="px-4 py-3 text-left font-medium">Occasion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holidays2025.map((holiday, index) => (
                      <tr key={holiday.date} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{holiday.date}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{holiday.occasion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-muted-foreground mt-4">
                * Additional holidays may be declared based on government notifications.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
